// src/pages/profile/ProfileHome.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Stack,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Button,
  Skeleton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Verified as VerifiedIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
  Badge as BadgeIcon,
  AlternateEmail as AlternateEmailIcon,
  AccountCircle as AccountCircleIcon,
  EmojiEvents as EmojiEventsIcon,
  Timer as TimerIcon,
  Gavel as GavelIcon,
  Groups as GroupsIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Firestore
import {
  getFirestore,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';

/* ---------------------------
   Helpers
---------------------------- */

// Obtén el rol principal del usuario
const getMainRole = (user, fallback = 'atleta') =>
  (user?.rol || user?.role || fallback).toString().toLowerCase();

// Formatea timestamp/Date a texto corto
const fmtDate = (tsOrDate) => {
  if (!tsOrDate) return '—';
  const d = tsOrDate instanceof Date ? tsOrDate : tsOrDate.toDate?.() || new Date(tsOrDate);
  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
};

/* ---------------------------
   Hook: Datos por Rol
---------------------------- */
const useRoleData = (user) => {
  const db = useMemo(() => getFirestore(), []);
  const role = getMainRole(user);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState({}); // datos específicos por rol
  const uid = user?.uid;

  useEffect(() => {
    let alive = true;
    const nowTs = Timestamp.fromDate(new Date());

    const load = async () => {
      if (!uid) {
        setLoading(false);
        return;
      }
      setLoading(true);

      try {
        // Estructura base para rellenar por rol
        const data = {};

        if (role === 'organizador') {
          // Próximos eventos organizados por el usuario
          const qEvents = query(
            collection(db, 'events'),
            where('organizerId', '==', uid),
            where('startDate', '>=', nowTs),
            orderBy('startDate', 'asc'),
            limit(5)
          );
          const snap = await getDocs(qEvents);
          data.upcomingEvents = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        }

        if (role === 'cronometrador') {
          // Asignaciones del cronometrador (event_roles por collectionGroup)
          const qRoles = query(
            collectionGroup(db, 'event_roles'),
            where('userId', '==', uid),
            where('role', '==', 'cronometrador'),
            orderBy('assignedAt', 'desc'),
            limit(5)
          );
          const rolesSnap = await getDocs(qRoles);
          const items = [];
          for (const rd of rolesSnap.docs) {
            const eventId = rd.ref.parent.parent.id; // padre del subdoc
            const evSnap = await getDoc(doc(db, 'events', eventId));
            items.push({
              id: rd.id,
              eventId,
              roleData: rd.data(),
              event: evSnap.exists() ? { id: evSnap.id, ...evSnap.data() } : null,
            });
          }
          data.timerAssignments = items;
        }

        if (role === 'juez') {
          // Asignaciones del juez
          const qRoles = query(
            collectionGroup(db, 'event_roles'),
            where('userId', '==', uid),
            where('role', '==', 'juez'),
            orderBy('assignedAt', 'desc'),
            limit(5)
          );
          const rolesSnap = await getDocs(qRoles);
          const items = [];
          for (const rd of rolesSnap.docs) {
            const eventId = rd.ref.parent.parent.id;
            const evSnap = await getDoc(doc(db, 'events', eventId));
            items.push({
              id: rd.id,
              eventId,
              roleData: rd.data(),
              event: evSnap.exists() ? { id: evSnap.id, ...evSnap.data() } : null,
            });
          }
          data.judgeAssignments = items;
        }

        if (role === 'entrenador') {
          // Próximos eventos donde participa su equipo o atletas (ejemplo básico)
          // Si tu esquema guarda teamId en users, puedes usarlo aquí:
          // const teamId = user?.teamId;
          // ...consulta events o tus colecciones relacionadas...
          data.coachSummary = { totalAthletes: user?.athletesCount ?? null, note: 'Conecta aquí tus consultas reales.' };
        }

        if (role === 'atleta') {
          // Próximas participaciones del atleta (vía event_roles)
          const qRoles = query(
            collectionGroup(db, 'event_roles'),
            where('userId', '==', uid),
            where('role', '==', 'atleta'),
            orderBy('assignedAt', 'desc'),
            limit(5)
          );
          const rolesSnap = await getDocs(qRoles);
          const items = [];
          for (const rd of rolesSnap.docs) {
            const eventId = rd.ref.parent.parent.id;
            const evSnap = await getDoc(doc(db, 'events', eventId));
            items.push({
              id: rd.id,
              eventId,
              roleData: rd.data(),
              event: evSnap.exists() ? { id: evSnap.id, ...evSnap.data() } : null,
            });
          }
          data.athleteEntries = items;
        }

        // Puedes añadir aquí federación / asociación / prensa según tu necesidad:
        // if (role === 'federacion') { ... }
        // if (role === 'asociacion') { ... }
        // if (role === 'prensa') { ... }

        if (alive) setPayload(data);
      } catch (e) {
        console.error('ProfileHome.useRoleData error:', e);
        if (alive) setPayload({});
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, [db, uid, role]);

  return { role, loading, data: payload };
};

/* ---------------------------
   Paneles por Rol (UI)
---------------------------- */

const OrganizerPanel = ({ events = [] }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>
      <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
      Próximos eventos que organizas
    </Typography>
    {events.length === 0 ? (
      <Typography variant="body2" color="text.secondary">No hay eventos próximos.</Typography>
    ) : (
      <List disablePadding>
        {events.map(ev => (
          <React.Fragment key={ev.id}>
            <ListItem>
              <ListItemText
                primary={ev.name || 'Evento sin nombre'}
                secondary={`${fmtDate(ev.startDate)} — ${ev.location || 'Sin sede'}`}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    )}
  </Paper>
);

const TimerPanel = ({ items = [] }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>
      <TimerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
      Últimas asignaciones (Cronometraje)
    </Typography>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">Sin asignaciones recientes.</Typography>
    ) : (
      <List disablePadding>
        {items.map(it => (
          <React.Fragment key={it.id}>
            <ListItem>
              <ListItemText
                primary={it.event?.name || 'Evento'}
                secondary={`Asignado: ${fmtDate(it.roleData?.assignedAt)} • Sede: ${it.event?.location || '—'}`}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    )}
  </Paper>
);

const JudgePanel = ({ items = [] }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>
      <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
      Últimas asignaciones (Jueceo)
    </Typography>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">Sin asignaciones recientes.</Typography>
    ) : (
      <List disablePadding>
        {items.map(it => (
          <React.Fragment key={it.id}>
            <ListItem>
              <ListItemText
                primary={it.event?.name || 'Evento'}
                secondary={`Rol: ${it.roleData?.subRole || 'Juez'} • ${fmtDate(it.roleData?.assignedAt)}`}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    )}
  </Paper>
);

const CoachPanel = ({ summary }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>
      <GroupsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
      Resumen de equipo
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {summary?.note || 'Integra aquí tus métricas (atletas, sesiones, estado).'}
    </Typography>
  </Paper>
);

const AthletePanel = ({ items = [] }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>
      <EmojiEventsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
      Próximas participaciones
    </Typography>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">No tienes participaciones próximas.</Typography>
    ) : (
      <List disablePadding>
        {items.map(it => (
          <React.Fragment key={it.id}>
            <ListItem>
              <ListItemText
                primary={it.event?.name || 'Evento'}
                secondary={`${fmtDate(it.event?.startDate)} • Prueba: ${it.roleData?.eventName || '—'}`}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    )}
  </Paper>
);

/* ---------------------------
   ProfileHome principal
---------------------------- */

const ProfileHome = () => {
  const { user } = useAuth();
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Usuario';
  const role = getMainRole(user);
  const subRole = user?.subRole || null;

  const { loading, data } = useRoleData(user);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Migas */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MUILink component={Link} underline="hover" color="inherit" to="/">
          Inicio
        </MUILink>
        <Typography color="text.primary">Perfil</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Avatar sx={{ width: 56, height: 56 }}>
          <AccountCircleIcon fontSize="large" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
            Hola, {displayName}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
            <Chip label={`Rol: ${role}`} size="small" icon={<BadgeIcon />} />
            {subRole && <Chip label={`Subrol: ${subRole}`} size="small" />}
            <Chip
              label={user?.emailVerified ? 'Correo verificado' : 'Correo no verificado'}
              size="small"
              color={user?.emailVerified ? 'success' : 'warning'}
              icon={user?.emailVerified ? <VerifiedIcon /> : <VerifiedOutlinedIcon />}
            />
          </Stack>
        </Box>

        <Button component={Link} to="/profile" variant="contained" startIcon={<SettingsIcon />}>
          Mi cuenta (ajustes)
        </Button>
      </Box>

      {/* Contenido por rol */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Resumen de cuenta */}
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Resumen de cuenta</Typography>
          <List disablePadding>
            <ListItem>
              <ListItemText primary="Correo" secondary={user?.email || '—'} />
            </ListItem>
            <Divider component="li" />
            <ListItem>
              <ListItemText primary="Rol principal" secondary={role} />
            </ListItem>
            {subRole && (
              <>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Subrol" secondary={subRole} />
                </ListItem>
              </>
            )}
          </List>
        </Paper>

        {/* Panel dinámico */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Cargando tu panel…</Typography>
              <Skeleton height={28} sx={{ mb: 1 }} />
              <Skeleton height={28} sx={{ mb: 1 }} />
              <Skeleton height={28} />
            </Paper>
          ) : (
            <>
              {role === 'organizador' && <OrganizerPanel events={data.upcomingEvents} />}
              {role === 'cronometrador' && <TimerPanel items={data.timerAssignments} />}
              {role === 'juez' && <JudgePanel items={data.judgeAssignments} />}
              {role === 'entrenador' && <CoachPanel summary={data.coachSummary} />}
              {role === 'atleta' && <AthletePanel items={data.athleteEntries} />}
              {/* Extiende aquí: federacion, asociacion, prensa, etc. */}
              {['federacion', 'asociacion', 'prensa'].includes(role) && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Vista de {role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Integra aquí los widgets y consultas específicas de {role}.
                  </Typography>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Stack>

      {/* Pie */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Tip: regresa a esta página desde el menú del usuario tocando tu nombre.
        </Typography>
      </Box>
    </Container>
  );
};

export default ProfileHome;
